import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./styles/globals.css";

import { AppShell } from "@/components/app-shell";
import { ProtectedRoute } from "@/components/protected-route";
import { useHydrateMe } from "@/hooks/use-me";

import LoginPage             from "@/pages/login";
import AuthCompletePage      from "@/pages/auth-complete";
import DashboardPage         from "@/pages/dashboard";
import AccountsPage          from "@/pages/accounts";
import AccountNewPage        from "@/pages/account-new";
import AccountImportPage     from "@/pages/account-import";
import AccountDetailPage     from "@/pages/account-detail";
import ContactsPage          from "@/pages/contacts";
import ContactNewPage        from "@/pages/contact-new";
import ContactImportPage     from "@/pages/contact-import";
import ContactDetailPage     from "@/pages/contact-detail";
import DealsPage             from "@/pages/deals";
import DealNewPage           from "@/pages/deal-new";
import DealImportPage        from "@/pages/deal-import";
import DealDetailPage        from "@/pages/deal-detail";
import LeadsPage                from "@/pages/leads";
import LeadNewPage              from "@/pages/lead-new";
import LeadImportPage           from "@/pages/lead-import";
import LeadDetailPage           from "@/pages/lead-detail";
import LeadsProspectApolloPage  from "@/pages/leads-prospect-apollo";
import LeadsProspectLinkedInPage from "@/pages/leads-prospect-linkedin";
import JobOrdersPage         from "@/pages/job-orders";
import JobOrderNewPage       from "@/pages/job-order-new";
import JobOrderDetailPage    from "@/pages/job-order-detail";
import CandidatesPage        from "@/pages/candidates";
import CandidateNewPage      from "@/pages/candidate-new";
import CandidateImportPage   from "@/pages/candidate-import";
import CandidateDetailPage   from "@/pages/candidate-detail";
import BenchAgingPage        from "@/pages/bench-aging";
import PlacementsPage        from "@/pages/placements";
import GovOpportunitiesPage  from "@/pages/gov-opportunities";
import GovOpportunityNewPage from "@/pages/gov-opportunity-new";
import GovOpportunityDetailPage from "@/pages/gov-opportunity-detail";
import ProjectsPage          from "@/pages/projects";
import ProjectNewPage        from "@/pages/project-new";
import ProjectDetailPage     from "@/pages/project-detail";
import TasksPage             from "@/pages/tasks";
import TaskNewPage           from "@/pages/task-new";
import MeetingsPage          from "@/pages/meetings";
import MeetingNewPage        from "@/pages/meeting-new";
import CallQueuePage         from "@/pages/call-queue";
import MyInboxPage           from "@/pages/my-inbox";
import SharedInboxPage       from "@/pages/shared-inbox";
import FeedbackPage          from "@/pages/feedback";
import EmailTemplatesPage    from "@/pages/email-templates";
import DocumentsPage         from "@/pages/documents";
import RepActivityPage       from "@/pages/rep-activity";
import ReportsPage           from "@/pages/reports";
import ReportViewPage        from "@/pages/report-view";
import ReportBuilderPage     from "@/pages/report-builder";
import DashboardsPage        from "@/pages/dashboards";
import MyRequestsPage        from "@/pages/my-requests";
import MyRequestNewPage      from "@/pages/my-request-new";
import ImportFromZohoPage    from "@/pages/import-from-zoho";
import ProductsPage          from "@/pages/products";
import ProductNewPage        from "@/pages/product-new";
import ProductDetailPage     from "@/pages/product-detail";
import SolutionsPage         from "@/pages/solutions";
import SolutionNewPage       from "@/pages/solution-new";
import SolutionDetailPage    from "@/pages/solution-detail";
import CompetitorsPage       from "@/pages/competitors";
import CompetitorNewPage     from "@/pages/competitor-new";
import CompetitorDetailPage  from "@/pages/competitor-detail";
import AdminPage             from "@/pages/admin";
import AdminUsersPage        from "@/pages/admin-users";
import AuditLogPage          from "@/pages/audit-log";
import AdminZohoPage         from "@/pages/admin-zoho";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

function App() {
  useHydrateMe();
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login"         element={<LoginPage />} />
        <Route path="/auth/complete" element={<AuthCompletePage />} />

        <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
          <Route index                          element={<DashboardPage />} />

          <Route path="accounts"                element={<AccountsPage />} />
          <Route path="accounts/new"            element={<AccountNewPage />} />
          <Route path="accounts/import"         element={<AccountImportPage />} />
          <Route path="accounts/:id"            element={<AccountDetailPage />} />

          <Route path="contacts"                element={<ContactsPage />} />
          <Route path="contacts/new"            element={<ContactNewPage />} />
          <Route path="contacts/import"         element={<ContactImportPage />} />
          <Route path="contacts/:id"            element={<ContactDetailPage />} />

          <Route path="deals"                   element={<DealsPage />} />
          <Route path="deals/new"               element={<DealNewPage />} />
          <Route path="deals/import"            element={<DealImportPage />} />
          <Route path="deals/:id"               element={<DealDetailPage />} />

          <Route path="leads"                   element={<LeadsPage />} />
          <Route path="leads/new"               element={<LeadNewPage />} />
          <Route path="leads/import"            element={<LeadImportPage />} />
          <Route path="leads/prospect/apollo"   element={<LeadsProspectApolloPage />} />
          <Route path="leads/prospect/linkedin" element={<LeadsProspectLinkedInPage />} />
          <Route path="leads/:id"               element={<LeadDetailPage />} />

          <Route path="job-orders"              element={<JobOrdersPage />} />
          <Route path="job-orders/new"          element={<JobOrderNewPage />} />
          <Route path="job-orders/:id"          element={<JobOrderDetailPage />} />

          <Route path="candidates"              element={<CandidatesPage />} />
          <Route path="candidates/new"          element={<CandidateNewPage />} />
          <Route path="candidates/import"       element={<CandidateImportPage />} />
          <Route path="candidates/bench"        element={<BenchAgingPage />} />
          <Route path="candidates/:id"          element={<CandidateDetailPage />} />

          <Route path="placements"              element={<PlacementsPage />} />

          <Route path="gov-opportunities"       element={<GovOpportunitiesPage />} />
          <Route path="gov-opportunities/new"   element={<GovOpportunityNewPage />} />
          <Route path="gov-opportunities/:id"   element={<GovOpportunityDetailPage />} />

          <Route path="projects"                element={<ProjectsPage />} />
          <Route path="projects/new"            element={<ProjectNewPage />} />
          <Route path="projects/:id"            element={<ProjectDetailPage />} />

          <Route path="tasks"                   element={<TasksPage />} />
          <Route path="tasks/new"               element={<TaskNewPage />} />

          <Route path="meetings"                element={<MeetingsPage />} />
          <Route path="meetings/new"            element={<MeetingNewPage />} />

          <Route path="call-queue"              element={<CallQueuePage />} />
          <Route path="my-inbox"                element={<MyInboxPage />} />
          <Route path="shared-inbox"            element={<SharedInboxPage />} />
          <Route path="feedback"                element={<FeedbackPage />} />

          <Route path="email-templates"         element={<EmailTemplatesPage />} />
          <Route path="documents"               element={<DocumentsPage />} />
          <Route path="rep-activity"            element={<RepActivityPage />} />

          <Route path="reports"                 element={<ReportsPage />} />
          <Route path="reports/new"             element={<ReportBuilderPage />} />
          <Route path="reports/canned/:id"      element={<ReportViewPage canned />} />
          <Route path="reports/:id"             element={<ReportViewPage />} />
          <Route path="reports/:id/edit"        element={<ReportBuilderPage />} />

          <Route path="dashboards"              element={<DashboardsPage />} />

          <Route path="my-requests"             element={<MyRequestsPage />} />
          <Route path="my-requests/new"         element={<MyRequestNewPage />} />

          <Route path="import-from-zoho"        element={<ImportFromZohoPage />} />

          <Route path="products"                element={<ProductsPage />} />
          <Route path="products/new"            element={<ProductNewPage />} />
          <Route path="products/:id"            element={<ProductDetailPage />} />

          <Route path="solutions"               element={<SolutionsPage />} />
          <Route path="solutions/new"           element={<SolutionNewPage />} />
          <Route path="solutions/:id"           element={<SolutionDetailPage />} />

          <Route path="competitors"             element={<CompetitorsPage />} />
          <Route path="competitors/new"         element={<CompetitorNewPage />} />
          <Route path="competitors/:id"         element={<CompetitorDetailPage />} />

          <Route path="apollo"                  element={<Navigate to="/leads/prospect/apollo" replace />} />
          <Route path="admin"                   element={<AdminPage />} />
          <Route path="admin/users"             element={<AdminUsersPage />} />
          <Route path="admin/audit"             element={<AuditLogPage />} />
          <Route path="admin/zoho"              element={<AdminZohoPage />} />
          <Route path="*"                       element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
